import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import '@/App.css';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Charges from './pages/Charges';
import WhatsApp from './pages/WhatsApp';
import TelegramConfig from './pages/TelegramConfig';
import Plans from './pages/Plans';
import Support from './pages/Support';
import Products from './pages/Products';
import Templates from './pages/Templates';
import Automation from './pages/Automation';
import Checkout from './pages/Checkout';
import Groups from './pages/Groups';
import History from './pages/History';
import Settings from './pages/Settings';
import Payment from './pages/Payment';
import MeusLinks from './pages/MeusLinks';
import Marketing from './pages/Marketing';
import Admin from './pages/Admin';
import Landing from './pages/Landing';
import Portal from './pages/Portal';
import { LojaProvider } from './pages/loja/LojaContext';
import LojaLayout from './pages/loja/LojaLayout';
import LojaHome from './pages/loja/LojaHome';
import LojaProductDetail from './pages/loja/LojaProductDetail';
import LojaCart from './pages/loja/LojaCart';
import LojaLogin from './pages/loja/LojaLogin';
import LojaRegister from './pages/loja/LojaRegister';
import LojaCheckout from './pages/loja/LojaCheckout';
import LojaOrders from './pages/loja/LojaOrders';
import LojaAdmin from './pages/loja/LojaAdmin';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OnlineStatus from './components/OnlineStatus';
import WhatsAppAlert from './components/WhatsAppAlert';
import SubscriptionExpired from './components/SubscriptionExpired';
import SubscriptionExpiryModal from './components/SubscriptionExpiryModal';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tempo de inatividade para logout (1 hora em milissegundos)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hora

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  // Atualizar última atividade
  const updateLastActivity = useCallback(() => {
    const now = Date.now();
    localStorage.setItem('lastActivity', now.toString());
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');
    setIsAuthenticated(false);
    setSubscriptionExpired(false);
    setUser(null);
  }, []);

  // Verificar inatividade e fazer logout se necessário
  useEffect(() => {
    if (!isAuthenticated) return;

    // Restaurar última atividade do localStorage
    const storedActivity = localStorage.getItem('lastActivity');
    if (storedActivity) {
      const storedTime = parseInt(storedActivity, 10);
      const timeSinceActivity = Date.now() - storedTime;
      
      // Se já passou mais de 1 hora desde a última atividade, fazer logout
      if (timeSinceActivity > INACTIVITY_TIMEOUT) {
        console.log('⏰ Sessão expirada por inatividade');
        handleLogout();
        return;
      }
    }

    // Verificar inatividade a cada minuto
    const checkInactivity = setInterval(() => {
      const currentActivity = parseInt(localStorage.getItem('lastActivity') || Date.now().toString(), 10);
      const timeSinceActivity = Date.now() - currentActivity;
      
      if (timeSinceActivity > INACTIVITY_TIMEOUT) {
        console.log('⏰ Logout por inatividade (1 hora)');
        handleLogout();
      }
    }, 60000); // Verificar a cada 1 minuto

    // Event listeners para detectar atividade do usuário
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, updateLastActivity, { passive: true });
    });

    // Atualizar atividade inicial
    updateLastActivity();

    return () => {
      clearInterval(checkInactivity);
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateLastActivity);
      });
    };
  }, [isAuthenticated, updateLastActivity, handleLogout]);

  const checkSubscription = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setIsAuthenticated(false);
      setSubscriptionExpired(false);
      return;
    }

    try {
      console.log('🔍 Checking subscription status...');
      const response = await axios.get(`${API_URL}/api/auth/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      console.log('📊 Subscription data:', JSON.stringify(data));
      console.log('📊 valid:', data.valid);
      console.log('📊 status:', data.status);
      console.log('📊 days_remaining:', data.days_remaining);
      setUser(data);
      setIsAuthenticated(true);
      
      // Check if subscription is valid - only block if valid is explicitly false
      if (data.valid === false) {
        console.log('❌ Subscription EXPIRED - blocking access');
        setSubscriptionExpired(true);
      } else {
        console.log('✅ Subscription VALID - allowing access');
        setSubscriptionExpired(false);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      // If token is invalid or any error, logout
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        setSubscriptionExpired(false);
      } else {
        // For other errors, still allow access but log it
        console.warn('Subscription check failed, allowing access');
        setIsAuthenticated(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      checkSubscription();
    } else {
      setLoading(false);
    }
  }, [checkSubscription]);

  // Routes that are allowed even with expired subscription
  const allowedExpiredRoutes = ['/plans', '/checkout', '/support', '/payment'];

  const ProtectedRoute = ({ children, allowExpired = false }) => {
    const location = useLocation();
    
    if (loading) {
      return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }

    // Check if subscription is expired and route is not allowed
    const isAllowedRoute = allowExpired || allowedExpiredRoutes.some(route => location.pathname.startsWith(route));
    
    if (subscriptionExpired && !isAllowedRoute) {
      return <SubscriptionExpired user={user} onLogout={handleLogout} />;
    }

    return (
      <>
        <WhatsAppAlert />
        {children}
      </>
    );
  };

  return (
    <ThemeProvider>
      <div className="App">
        <OnlineStatus />
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Portal />} />
          <Route path="/gestor" element={<Landing />} />
          <Route path="/login" element={<Login setAuth={setIsAuthenticated} onLogin={checkSubscription} />} />
          <Route path="/register" element={<Register setAuth={setIsAuthenticated} onLogin={checkSubscription} />} />
          {/* Loja MARKIMAGEM routes (isolated with LojaProvider) */}
          <Route path="/loja" element={<LojaProvider><LojaLayout /></LojaProvider>}>
            <Route index element={<LojaHome />} />
            <Route path="product/:id" element={<LojaProductDetail />} />
            <Route path="cart" element={<LojaCart />} />
            <Route path="login" element={<LojaLogin />} />
            <Route path="register" element={<LojaRegister />} />
            <Route path="checkout" element={<LojaCheckout />} />
            <Route path="orders" element={<LojaOrders />} />
            <Route path="admin" element={<LojaAdmin />} />
          </Route>
          <Route path="/register" element={<Register setAuth={setIsAuthenticated} onLogin={checkSubscription} />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/charges" element={<ProtectedRoute><Charges /></ProtectedRoute>} />
          <Route path="/whatsapp" element={<ProtectedRoute><WhatsApp /></ProtectedRoute>} />
          <Route path="/telegram" element={<ProtectedRoute><TelegramConfig /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
          <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
          <Route path="/automation" element={<ProtectedRoute><Automation /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/meus-links" element={<ProtectedRoute><MeusLinks /></ProtectedRoute>} />
          <Route path="/marketing" element={<ProtectedRoute><Marketing /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute allowExpired={true}><Plans /></ProtectedRoute>} />
          <Route path="/checkout/:planId" element={<ProtectedRoute allowExpired={true}><Checkout /></ProtectedRoute>} />
          <Route path="/payment" element={<ProtectedRoute allowExpired={true}><Payment /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute allowExpired={true}><Support /></ProtectedRoute>} />
        </Routes>
        <PWAInstallPrompt />
        {isAuthenticated && <SubscriptionExpiryModal />}
      </BrowserRouter>
      <Toaster richColors position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;
